import { AxiosError, AxiosResponse } from 'axios';
import { screeningApi } from 'pages/api/services/baseurl';
import {
  ApplicantDetail,
  ApplicantFilter,
  ApplicantSummary,
  ApplyResponse,
  AuthResponse,
  BoardResponse,
  CompanyProfile,
  DecisionInput,
  Employer,
  InterviewReview,
  InterviewStartResponse,
  InterviewTokenInfo,
  Job,
  JobFormInput,
  JobSettingsInput,
  JobSummary,
  MapDirectory,
  LoginInput,
  MeResponse,
  OnboardingAnswersInput,
  OnboardingQuestion,
  PublicJob,
  PublishJobResponse,
  RegenerateSpecsResponse,
  SendInviteResponse,
  SignupInput,
  SpecItem,
  ApplicationQuestion,
  UpdateMeInput,
  VerificationRequired,
  VerifyEmailInput,
  BillingCheckoutInput,
  BillingCheckoutResponse,
  BillingPlansResponse,
  BillingStatusResponse,
  ScreeningBilling,
  ScreeningTeam,
  TeamInvitePreview,
  MapResponse,
  MapPerson,
  MapSummary,
  MapOverview,
  MapTeamView,
  MapRoleView,
  MapPersonView,
  MapPersonCard,
  MapSentence,
  AtsProvider,
  MapSourcesResponse,
  ScreeningShareLink,
  SharePacket,
} from './types';
import { MapHomeV2, MapPersonV2, MapRoleV2, MapSearchV2 } from './mapV2';

export type MapActionV2 = {
  kind: 'nudge' | 'bulk_invite' | 'invite_person' | 'reminder';
  person_ids?: string[];
  cohort_id?: string;
  count?: number;
};

/**
 * A single error type surfaced to the UI. There are no structured field
 * errors, so screens show `message` at form level.
 */
export class ApiError extends Error {
  httpStatus: number;

  constructor(message: string, httpStatus: number) {
    super(message);
    this.name = 'ApiError';
    this.httpStatus = httpStatus;
  }
}

const unwrap = <T>(res: AxiosResponse<{ data?: T } | T>): T => {
  const body = res.data as { data?: T } & T;
  if (
    body &&
    typeof body === 'object' &&
    'data' in body &&
    (body as { data?: T }).data !== undefined
  ) {
    return (body as { data: T }).data;
  }
  return body as T;
};

const toApiError = (error: unknown): ApiError => {
  const axiosErr = error as AxiosError<{ message?: string }>;
  const status = axiosErr.response?.status ?? 0;
  const message =
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Something went wrong. Try again.';
  return new ApiError(message, status);
};

const request = async <T>(fn: () => Promise<AxiosResponse>): Promise<T> => {
  try {
    const res = await fn();
    return unwrap<T>(res);
  } catch (error) {
    throw toApiError(error);
  }
};

// Blocking inference calls need a generous timeout.
const LONG_TIMEOUT = 120000;

export interface ProviderQuote {
  text: string;
  source: string;
}

export interface ProviderRecommendation {
  person_id: string;
  full_name: string;
  band: 'strong' | 'good' | 'stretch';
  quotes: ProviderQuote[];
  gap: string | null;
  cv_only?: boolean;
}

export interface ProviderRole {
  id: string;
  title: string;
  description?: string;
  group_name?: string | null;
  status: string;
  published?: boolean;
  considered?: number;
  changed?: boolean;
  recommendations?: ProviderRecommendation[];
}

export interface ProviderHome {
  title: string;
  lead: string;
  directory?: MapDirectory;
  roles: ProviderRole[];
  groups: Array<{ id: string; name: string; meta: string }>;
}

export interface CohortCreated {
  cohort: { id: string; name: string };
  link_path: string;
  message: string;
}

export interface ProviderCheck {
  name: string;
  state: 'yes' | 'no' | 'uploaded';
}

export interface ProviderPersonRecord {
  id: string;
  full_name: string;
  quotes?: ProviderQuote[];
  checks?: ProviderCheck[];
  career_path?: Array<{ kind: string; label: string; sentence: string }>;
  feedback?: { strengths?: string[]; next?: string } | null;
  analysis?: { reading?: string; thin?: string | null } | null;
  requests?: Array<{ sentence: string }>;
  profile_token?: string;
  matches?: Array<ProviderRecommendation & { title?: string }>;
}

export interface PublicProfile {
  company_name: string;
  cohort_name?: string | null;
  full_name: string;
  profile_ready: boolean;
  notified: boolean;
  feedback?: { strengths?: string[]; next?: string } | null;
  career_path?: Array<{ kind: string; label: string; sentence: string }>;
  quotes?: ProviderQuote[];
  audio_ready?: boolean;
  analysis?: { reading?: string; thin?: string | null } | null;
  requests?: Array<{ sentence: string }>;
  matches?: Array<ProviderRecommendation & { title?: string }>;
  files?: Array<{ kind: string; name: string; note?: string }>;
}

export interface PublicPacket {
  id: string;
  title: string;
  company_name: string;
  people: ProviderRecommendation[];
}

export const screeningClient = {
  /* ---------------------------------------------------------------- */
  /* Auth & account                                                    */
  /* ---------------------------------------------------------------- */
  signup: (payload: SignupInput) =>
    request<AuthResponse | VerificationRequired>(() =>
      screeningApi.post('/screening/signup', payload)
    ),

  login: (payload: LoginInput) =>
    request<AuthResponse | VerificationRequired>(() =>
      screeningApi.post('/screening/login', payload)
    ),

  googleAuth: (payload: { credential: string }) =>
    request<AuthResponse>(() =>
      screeningApi.post('/screening/auth/google', payload)
    ),

  forgotPassword: (payload: { email: string }) =>
    request<{ message: string }>(() =>
      screeningApi.post('/screening/forgot-password', payload)
    ),

  resetPassword: (payload: { email: string; code: string; password: string }) =>
    request<{ message: string }>(() =>
      screeningApi.post('/screening/reset-password', payload)
    ),

  verifyEmail: (payload: VerifyEmailInput) =>
    request<AuthResponse & { email_verified: boolean }>(() =>
      screeningApi.post('/screening/verify-email', payload)
    ),

  resendVerification: (payload: { email: string }) =>
    request<VerificationRequired>(() =>
      screeningApi.post('/screening/resend-verification', payload)
    ),

  me: () => request<MeResponse>(() => screeningApi.get('/screening/me')),

  updateMe: (payload: UpdateMeInput) =>
    request<Employer>(() => screeningApi.patch('/screening/me', payload)),

  /** Per-user tip / walkthrough progress. */
  getUiProgress: () =>
    request<{ ui_progress: import('productTips').UiProgress }>(() =>
      screeningApi.get('/screening/me/ui-progress')
    ),

  patchUiProgress: (payload: Record<string, unknown>) =>
    request<{ ui_progress: import('productTips').UiProgress }>(() =>
      screeningApi.patch('/screening/me/ui-progress', payload)
    ),

  /* ---------------------------------------------------------------- */
  /* Onboarding                                                        */
  /* ---------------------------------------------------------------- */
  onboardingQuestions: () =>
    request<{ questions: OnboardingQuestion[] }>(() =>
      screeningApi.get('/screening/onboarding/questions')
    ),

  onboardingStep: (payload: OnboardingAnswersInput) =>
    request<{ company_profile: CompanyProfile }>(() =>
      screeningApi.post('/screening/onboarding/step', payload)
    ),

  onboardingSubmit: (payload: OnboardingAnswersInput) =>
    request<{ company_profile: CompanyProfile }>(() =>
      screeningApi.post('/screening/onboarding/submit', payload)
    ),

  setupTeams: (payload: { teams: unknown[] }) =>
    request<{ teams: ScreeningTeam[] }>(() =>
      screeningApi.post('/screening/teams/setup', payload)
    ),

  listTeams: () =>
    request<{ teams: ScreeningTeam[] }>(() =>
      screeningApi.get('/screening/teams')
    ),

  getTeam: (teamId: string) =>
    request<{ team: ScreeningTeam }>(() =>
      screeningApi.get(`/screening/teams/${teamId}`)
    ),

  resendTeamInvite: (teamId: string) =>
    request<unknown>(() =>
      screeningApi.post(`/screening/teams/${teamId}/invite`)
    ),

  teamOnboardingQuestions: (teamId: string) =>
    request<{
      questions: OnboardingQuestion[];
      company_profile: CompanyProfile | null;
    }>(() =>
      screeningApi.get(`/screening/teams/${teamId}/onboarding/questions`)
    ),

  teamOnboardingStep: (teamId: string, payload: OnboardingAnswersInput) =>
    request<{
      team_profile: CompanyProfile;
      company_profile: CompanyProfile | null;
    }>(() =>
      screeningApi.post(`/screening/teams/${teamId}/onboarding/step`, payload)
    ),

  teamOnboardingSubmit: (teamId: string, payload: OnboardingAnswersInput) =>
    request<{ team: ScreeningTeam; team_profile: CompanyProfile }>(() =>
      screeningApi.post(`/screening/teams/${teamId}/onboarding/submit`, payload)
    ),

  previewTeamInvite: (token: string) =>
    request<TeamInvitePreview>(() =>
      screeningApi.get(`/screening/invite/${token}`)
    ),

  acceptTeamInvite: (token: string, password: string) =>
    request<AuthResponse>(() =>
      screeningApi.post(`/screening/invite/${token}/accept`, { password })
    ),

  listMap: (params?: { band?: string; team_id?: string; job_id?: string }) =>
    request<MapResponse>(() => screeningApi.get('/screening/maps', { params })),

  mapSummary: () =>
    request<MapSummary>(() => screeningApi.get('/screening/maps/summary')),

  mapOverview: () =>
    request<MapOverview>(() => screeningApi.get('/screening/maps/overview')),

  mapTeam: (teamId: string) =>
    request<MapTeamView>(() =>
      screeningApi.get(`/screening/maps/teams/${teamId}`)
    ),

  mapRole: (roleId: string) =>
    request<MapRoleView>(() =>
      screeningApi.get(`/screening/maps/roles/${roleId}`)
    ),

  mapPersonView: (personId: string, roleId?: string) =>
    request<MapPersonView>(() =>
      screeningApi.get(`/screening/maps/people/${personId}`, {
        params: roleId ? { role: roleId } : undefined,
      })
    ),

  mapSearch: (q: string, outside?: boolean) =>
    request<{
      query: string;
      reading?: string;
      roles: Array<
        | MapTeamView['roles'][number]
        | { id: string; title: string; status?: string; type?: string }
      >;
      people: Array<MapPersonCard | ProviderRecommendation>;
      groups?: Array<{ id: string; name: string; type?: string }>;
    }>(() =>
      screeningApi.get('/screening/maps/search', {
        params: { q, outside: outside ? '1' : undefined },
      })
    ),

  mapShortlist: () =>
    request<{ people: MapPersonCard[] }>(() =>
      screeningApi.get('/screening/maps/shortlist')
    ),

  mapInvitePerson: (
    personId: string,
    payload: { job_id?: string; reminder?: boolean }
  ) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/people/${personId}/invite`, payload)
    ),

  mapShortlistPerson: (
    personId: string,
    payload: { job_id?: string; paper_override?: boolean }
  ) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/people/${personId}/shortlist`, payload)
    ),

  mapUnshortlistPerson: (personId: string, jobId?: string) =>
    request<MapSentence>(() =>
      screeningApi.delete(`/screening/maps/people/${personId}/shortlist`, {
        params: { job_id: jobId },
      })
    ),

  mapPassPerson: (personId: string, payload: { job_id?: string }) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/people/${personId}/pass`, payload)
    ),

  mapReassignPerson: (personId: string, jobId: string) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/people/${personId}/reassign`, {
        job_id: jobId,
      })
    ),

  mapRequestPerson: (personId: string, jobId?: string) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/people/${personId}/request`, {
        job_id: jobId,
      })
    ),

  mapBulkInvite: (payload: {
    job_id?: string;
    team_id?: string;
    threshold?: number;
  }) =>
    request<MapSentence>(() =>
      payload.job_id
        ? screeningApi.post(
            `/screening/maps/roles/${payload.job_id}/invite`,
            payload
          )
        : screeningApi.post(
            `/screening/maps/teams/${payload.team_id}/invite`,
            payload
          )
    ),

  mapBulkShortlist: (roleId: string, n?: number) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/roles/${roleId}/shortlist`, { n })
    ),

  mapSendShortlist: () =>
    request<MapSentence>(() =>
      screeningApi.post('/screening/maps/shortlist/send')
    ),

  mapHomeV2: () =>
    request<MapHomeV2>(() => screeningApi.get('/screening/maps/v2/home')),

  mapSearchV2: (params: { q: string; drop?: string; sort?: string }) =>
    request<MapSearchV2>(() =>
      screeningApi.get('/screening/maps/v2/search', { params })
    ),

  mapRoleV2: (roleId: string) =>
    request<MapRoleV2>(() =>
      screeningApi.get(`/screening/maps/v2/roles/${roleId}`)
    ),

  mapPersonV2: (personId: string, roleId?: string) =>
    request<MapPersonV2>(() =>
      screeningApi.get(`/screening/maps/v2/people/${personId}`, {
        params: roleId ? { role: roleId } : undefined,
      })
    ),

  mapShortlistAddV2: (roleId: string, personId: string) =>
    request<MapSentence>(() =>
      screeningApi.post(
        `/screening/maps/v2/roles/${roleId}/shortlist/${personId}`
      )
    ),

  mapShortlistRemoveV2: (roleId: string, personId: string) =>
    request<MapSentence>(() =>
      screeningApi.delete(
        `/screening/maps/v2/roles/${roleId}/shortlist/${personId}`
      )
    ),

  mapShortlistSendV2: (roleId: string, note: string) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/v2/roles/${roleId}/shortlist/send`, {
        note,
      })
    ),

  mapAskV2: (personId: string, sentence: string) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/v2/people/${personId}/ask`, {
        sentence,
      })
    ),

  mapPlaceV2: (personId: string, employer?: string) =>
    request<MapSentence>(() =>
      screeningApi.post(`/screening/maps/v2/people/${personId}/place`, {
        employer,
      })
    ),

  mapActionV2: (payload: MapActionV2) =>
    request<MapSentence>(() =>
      screeningApi.post('/screening/maps/v2/actions', payload)
    ),

  mapPatchWeights: (
    roleId: string,
    weights: Array<{ name?: string; id?: string; value: number }>
  ) =>
    request<MapRoleView>(() =>
      screeningApi.patch(`/screening/maps/roles/${roleId}/weights`, { weights })
    ),

  mapInterest: (
    personId: string,
    payload: {
      verdict: 'interested' | 'pass';
      team_id?: string;
      job_id?: string;
    }
  ) =>
    request<{ person_id: string; verdict: string; demo?: boolean }>(() =>
      screeningApi.post(`/screening/maps/people/${personId}/interest`, payload)
    ),

  ingestMap: (form: FormData) =>
    request<{
      created: number;
      jobs: number;
      applications: number;
    }>(() =>
      screeningApi.post('/screening/maps/ingest', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ),

  mapSources: () =>
    request<MapSourcesResponse>(() =>
      screeningApi.get('/screening/maps/sources')
    ),

  disconnectSource: (provider: AtsProvider) =>
    request<{ provider: AtsProvider; disconnected: boolean }>(() =>
      screeningApi.delete(`/screening/maps/sources/${provider}`)
    ),

  connectWorkable: (payload: { subdomain: string; token: string }) =>
    request<{
      provider: 'workable';
      subdomain?: string;
      webhook_url?: string | null;
    }>(() => screeningApi.post('/screening/maps/sources/workable', payload)),

  syncWorkable: () =>
    request<unknown>(() =>
      screeningApi.post('/screening/maps/sources/workable/sync')
    ),

  connectWorkday: (payload: {
    raas_url: string;
    username: string;
    password: string;
  }) =>
    request<unknown>(() =>
      screeningApi.post('/screening/maps/sources/workday', payload)
    ),

  syncWorkday: () =>
    request<unknown>(() =>
      screeningApi.post('/screening/maps/sources/workday/sync')
    ),

  connectSeamless: (payload: { client_id: string; client_secret: string }) =>
    request<unknown>(() =>
      screeningApi.post('/screening/maps/sources/seamless', payload)
    ),

  connectAshby: (payload: { api_key: string }) =>
    request<unknown>(() =>
      screeningApi.post('/screening/maps/sources/ashby', payload)
    ),

  syncAshby: () =>
    request<unknown>(() =>
      screeningApi.post('/screening/maps/sources/ashby/sync')
    ),

  connectGreenhouse: (payload: { client_id: string; client_secret: string }) =>
    request<unknown>(() =>
      screeningApi.post('/screening/maps/sources/greenhouse', payload)
    ),

  syncGreenhouse: () =>
    request<unknown>(() =>
      screeningApi.post('/screening/maps/sources/greenhouse/sync')
    ),

  queryMap: (payload: { query: string; band?: string; team_id?: string }) =>
    request<{ results: MapPerson[]; query: string }>(() =>
      screeningApi.post('/screening/maps/query', payload)
    ),

  rematchMap: (payload?: { job_id?: string }) =>
    request<{ matched: number; people: number; jobs: number }>(() =>
      screeningApi.post('/screening/maps/rematch', payload || {})
    ),

  intakePool: (payload: { rows: unknown[] }) =>
    request<{
      processed: number;
      created: number;
      updated: number;
      skipped: number;
    }>(() => screeningApi.post('/screening/pools/intake', payload)),

  /* ---------------------------------------------------------------- */
  /* Billing                                                           */
  /* ---------------------------------------------------------------- */
  billingStatus: () =>
    request<BillingStatusResponse>(() =>
      screeningApi.get('/screening/billing/status')
    ),

  billingPortal: () =>
    request<{ url: string }>(() =>
      screeningApi.post('/screening/billing/portal')
    ),

  updateBillingPlan: (payload: BillingCheckoutInput) =>
    request<BillingStatusResponse & { url?: string }>(() =>
      screeningApi.post('/screening/billing/plan', payload)
    ),

  billingPlans: () =>
    request<BillingPlansResponse>(() =>
      screeningApi.get('/screening/billing/plans')
    ),

  setBillingCountry: (payload: { country_code: string }) =>
    request<{
      country_code: string | null;
      billing_region: string | null;
      billing: ScreeningBilling;
    }>(() => screeningApi.post('/screening/billing/country', payload)),

  billingCheckout: (payload: BillingCheckoutInput) =>
    request<BillingCheckoutResponse>(() =>
      screeningApi.post('/screening/billing/checkout', payload)
    ),

  redeemEnterprise: (payload: { code: string }) =>
    request<{
      country_code: string | null;
      billing_region: string | null;
      billing: ScreeningBilling;
    }>(() => screeningApi.post('/screening/billing/enterprise', payload)),

  billingConfirm: (payload: { reference?: string; session_id?: string }) =>
    request<{
      country_code: string | null;
      billing_region: string | null;
      billing: ScreeningBilling;
    }>(() => screeningApi.post('/screening/billing/confirm', payload)),

  /* ---------------------------------------------------------------- */
  /* Jobs                                                              */
  /* ---------------------------------------------------------------- */
  listJobs: () =>
    request<{ jobs: JobSummary[] }>(() => screeningApi.get('/screening/jobs')),

  getJob: (id: string) =>
    request<{ job: Job }>(() => screeningApi.get(`/screening/jobs/${id}`)),

  createJob: (payload: JobFormInput) =>
    request<{ job: Job }>(() => screeningApi.post('/screening/jobs', payload)),

  updateJob: (
    id: string,
    payload: Partial<JobFormInput> & {
      spec_items?: SpecItem[];
      application_questions?: ApplicationQuestion[];
    }
  ) =>
    request<{ job: Job }>(() =>
      screeningApi.patch(`/screening/jobs/${id}`, payload)
    ),

  regenerateSpecs: (id: string) =>
    request<RegenerateSpecsResponse>(() =>
      screeningApi.post(`/screening/jobs/${id}/regenerate-specs`, undefined, {
        timeout: LONG_TIMEOUT,
      })
    ),

  deleteJob: (id: string) =>
    request<{ deleted: boolean }>(() =>
      screeningApi.delete(`/screening/jobs/${id}`)
    ),

  publishJob: (id: string) =>
    request<PublishJobResponse>(() =>
      screeningApi.post(`/screening/jobs/${id}/publish`)
    ),

  updateSettings: (id: string, payload: JobSettingsInput) =>
    request<{ job: Job }>(() =>
      screeningApi.patch(`/screening/jobs/${id}/settings`, payload)
    ),

  /* ---------------------------------------------------------------- */
  /* Applicants & decisions                                            */
  /* ---------------------------------------------------------------- */
  listApplicants: (jobId: string, filter: ApplicantFilter) =>
    request<{ applicants: ApplicantSummary[] }>(() =>
      screeningApi.get(`/screening/jobs/${jobId}/applicants`, {
        params: { filter },
      })
    ),

  getApplicant: (listId: string, candidateId: string) =>
    request<ApplicantDetail>(() =>
      screeningApi.get(`/screening/jobs/${listId}/applicants/${candidateId}`)
    ),

  setDecision: (listId: string, candidateId: string, payload: DecisionInput) =>
    request<unknown>(() =>
      screeningApi.patch(
        `/screening/jobs/${listId}/applicants/${candidateId}/decision`,
        payload
      )
    ),

  sendInvite: (listId: string, candidateId: string) =>
    request<SendInviteResponse>(() =>
      screeningApi.post(
        `/screening/jobs/${listId}/applicants/${candidateId}/send-invite`
      )
    ),

  getApplicantInterview: (listId: string, candidateId: string) =>
    request<InterviewReview>(() =>
      screeningApi.get(
        `/screening/jobs/${listId}/applicants/${candidateId}/interview`
      )
    ),

  /* ---------------------------------------------------------------- */
  /* Public: board                                                     */
  /* ---------------------------------------------------------------- */
  board: (employerToken: string, publicOrigin?: string) =>
    request<BoardResponse>(() =>
      screeningApi.get(`/boards/${employerToken}/jobs`, {
        headers: publicOrigin
          ? { 'X-HireMe-Public-Origin': publicOrigin }
          : undefined,
      })
    ),

  /* ---------------------------------------------------------------- */
  /* Public: apply                                                     */
  /* ---------------------------------------------------------------- */
  publicJob: (publicId: string) =>
    request<PublicJob>(() => screeningApi.get(`/jobs/${publicId}`)),

  apply: (publicId: string, form: FormData) =>
    request<ApplyResponse>(() =>
      screeningApi.post(`/jobs/${publicId}/apply`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ),

  /* ---------------------------------------------------------------- */
  /* Public: interview                                                 */
  /* ---------------------------------------------------------------- */
  interviewInfo: (token: string) =>
    request<InterviewTokenInfo>(() => screeningApi.get(`/interviews/${token}`)),

  prepareInterview: (token: string, options?: { remint?: boolean }) =>
    request<InterviewStartResponse>(() =>
      screeningApi.post(
        `/interviews/${token}/prepare`,
        options?.remint ? { remint: true } : undefined
      )
    ),

  startInterview: (token: string) =>
    request<InterviewStartResponse>(() =>
      screeningApi.post(`/interviews/${token}/start`)
    ),

  /* ---------------------------------------------------------------- */
  /* Client and role share                                             */
  /* ---------------------------------------------------------------- */
  createShare: (payload: {
    scope: 'role' | 'client';
    job_id?: string;
    team_id?: string;
    password?: string;
  }) =>
    request<{ share: ScreeningShareLink }>(() =>
      screeningApi.post('/screening/shares', payload)
    ),

  listShares: (params: { job_id?: string; team_id?: string }) =>
    request<{ shares: ScreeningShareLink[] }>(() =>
      screeningApi.get('/screening/shares', { params })
    ),

  revokeShare: (id: string) =>
    request<{ ok: boolean }>(() =>
      screeningApi.delete(`/screening/shares/${id}`)
    ),

  rotateSharePassword: (id: string, password: string) =>
    request<{ share: ScreeningShareLink }>(() =>
      screeningApi.patch(`/screening/shares/${id}`, { password })
    ),

  publicShare: (token: string) =>
    request<SharePacket>(() =>
      screeningApi.get(`/screening/share/${token}`, { withCredentials: true })
    ),

  unlockShare: (token: string, password: string) =>
    request<SharePacket>(() =>
      screeningApi.post(
        `/screening/share/${token}/unlock`,
        { password },
        { withCredentials: true }
      )
    ),

  providerHome: () =>
    request<ProviderHome>(() => screeningApi.get('/screening/provider/home')),

  createCohort: (payload: { name: string; close_date?: string }) =>
    request<CohortCreated>(() =>
      screeningApi.post('/screening/cohorts', payload)
    ),

  describeRole: (payload: {
    text: string;
    title?: string;
    cohort_id?: string;
    publish?: boolean;
  }) =>
    request<{ role: ProviderRole }>(() =>
      screeningApi.post('/screening/provider/roles', payload)
    ),

  publishProviderRole: (id: string) =>
    request<{ role: ProviderRole }>(() =>
      screeningApi.post(`/screening/provider/roles/${id}/publish`)
    ),

  providerPerson: (id: string) =>
    request<{ person: ProviderPersonRecord }>(() =>
      screeningApi.get(`/screening/provider/people/${id}`)
    ),

  attestCheck: (
    id: string,
    payload: { name: string; state: 'yes' | 'no' | 'uploaded' }
  ) =>
    request<{ person_id: string; checks: ProviderCheck[] }>(() =>
      screeningApi.post(`/screening/provider/people/${id}/checks`, payload)
    ),

  askProvider: (id: string, sentence: string) =>
    request<{ person_id: string }>(() =>
      screeningApi.post(`/screening/provider/people/${id}/requests`, {
        sentence,
      })
    ),

  recordPlacement: (
    id: string,
    payload: { employer: string; start_date: string }
  ) =>
    request<{ person_id: string }>(() =>
      screeningApi.post(`/screening/provider/people/${id}/placement`, payload)
    ),

  createProviderReferral: (payload: {
    full_name: string;
    relationship: 'school' | 'worked_with' | 'know';
    note?: string;
  }) =>
    request<{
      referral: { id: string; full_name: string };
      link_path: string;
      message: string;
    }>(() => screeningApi.post('/screening/provider/referrals', payload)),

  createProviderShortlist: (roleId: string) =>
    request<{ packet: { id: string }; link_path: string }>(() =>
      screeningApi.post(`/screening/provider/roles/${roleId}/shortlist`)
    ),

  publicCohort: (cohortId: string) =>
    request<{ company_name: string; cohort_name: string; message: string }>(
      () => screeningApi.get(`/cohorts/${cohortId}`)
    ),

  joinCohort: (cohortId: string, full_name: string) =>
    request<{
      company_name: string;
      cohort_name: string;
      profile_token: string;
      profile_path: string;
      interview_path: string;
    }>(() => screeningApi.post(`/cohorts/${cohortId}/join`, { full_name })),

  publicProfile: (token: string) =>
    request<PublicProfile>(() => screeningApi.get(`/profiles/${token}`)),

  finishProviderConversation: (token: string, lines: string[]) =>
    request<PublicProfile>(() =>
      screeningApi.post(`/profiles/${token}/conversation`, { lines })
    ),

  addProfileFile: (
    token: string,
    payload: { kind: 'cv' | 'certificate'; name: string }
  ) =>
    request<PublicProfile>(() =>
      screeningApi.post(`/profiles/${token}/files`, payload)
    ),

  publicPacket: (token: string) =>
    request<PublicPacket>(() => screeningApi.get(`/packets/${token}`)),

  packetAction: (
    token: string,
    payload: {
      person_id: string;
      action: 'request_interview' | 'pass' | 'question';
      reason?: string;
    }
  ) =>
    request<{ ok: boolean }>(() =>
      screeningApi.post(`/packets/${token}/actions`, payload)
    ),

  providerRole: (id: string) =>
    request<{ role: ProviderRole }>(() =>
      screeningApi.get(`/screening/provider/roles/${id}`)
    ),

  providerGroup: (id: string) =>
    request<{
      title: string;
      lead: string;
      people: ProviderRecommendation[];
    }>(() => screeningApi.get(`/screening/provider/groups/${id}`)),

  correctPath: (token: string, payload: { kind: string; sentence: string }) =>
    request<PublicProfile>(() =>
      screeningApi.post(`/profiles/${token}/path`, payload)
    ),

  inviteOrganisation: (payload: { name: string; note?: string }) =>
    request<{ link_path: string; message: string }>(() =>
      screeningApi.post('/screening/provider/invites', payload)
    ),
};

export default screeningClient;
